import { captureError, captureWarning, sentryLog } from '@/utils/sentry';

const LOG_CTX_FLAG = '__benLogCtx';

/**
 * Create a mutable log context object that the request/response interceptors
 * can write API metadata back to. Pass the returned object as
 * `__logContext` to `request(...)` AND as the context arg to
 * `logger.info/warn/error` — both sides share the same reference, so business
 * logs automatically pick up `api / method / url / httpStatus / outcome /
 * duration / requestId` written by the interceptor.
 *
 * Constraints:
 *  - Do NOT spread the result (`{ ...ctx, extra }`) — that drops the flag and
 *    breaks the writeback chain. Use `Object.assign(ctx, extra)` if you need
 *    to merge in extra fields.
 *  - Do NOT share one ctx across concurrent `Promise.all` requests — see spec §6.2.
 */
export function createApiLogContext(
  initial?: Record<string, unknown>,
): Record<string, unknown> {
  const target: Record<string, unknown> = { ...(initial ?? {}) };
  Object.defineProperty(target, LOG_CTX_FLAG, {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  return target;
}

/**
 * Returns true iff `value` is a plain object created by `createApiLogContext`.
 * Class instances, arrays, primitives, and objects with an enumerable
 * `__benLogCtx` property are all rejected.
 */
export function isApiLogContext(
  value: unknown,
): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (Object.getPrototypeOf(value) !== Object.prototype) return false;
  const desc = Object.getOwnPropertyDescriptor(value, LOG_CTX_FLAG);
  return !!desc && desc.value === true && desc.enumerable === false;
}

/**
 * 日志级别定义
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 99,
}

// 默认配置：生产环境只显示 INFO 及以上
const DEFAULT_CONFIG = {
  level: process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG,
  enableTimestamp: true,
};

class Logger {
  private moduleName: string;

  constructor(moduleName: string) {
    this.moduleName = moduleName;
  }

  private format(level: string, message: string): string[] {
    const timestamp = DEFAULT_CONFIG.enableTimestamp
      ? `[${new Date().toISOString()}]`
      : '';
    return [`${timestamp} [${this.moduleName}] [${level}]`, message];
  }

  debug(message: string, context?: Record<string, any>) {
    if (DEFAULT_CONFIG.level <= LogLevel.DEBUG) {
      console.debug(...this.format('DEBUG', message), context ?? '');
      sentryLog('debug', `[${this.moduleName}] ${message}`, {
        module: this.moduleName,
        ...context,
      });
    }
  }

  info(message: string, context?: Record<string, any>) {
    if (DEFAULT_CONFIG.level <= LogLevel.INFO) {
      console.info(...this.format('INFO', message), context ?? '');
      sentryLog('info', `[${this.moduleName}] ${message}`, {
        module: this.moduleName,
        ...context,
      });
    }
  }

  warn(message: string, context?: Record<string, any> | any) {
    if (DEFAULT_CONFIG.level <= LogLevel.WARN) {
      console.warn(...this.format('WARN', message), context ?? '');
      const attrs =
        context && typeof context === 'object' && !Array.isArray(context)
          ? { module: this.moduleName, ...context }
          : { module: this.moduleName };
      captureWarning(`[${this.moduleName}] ${message}`, attrs);
    }
  }

  error(message: string, error?: any, context?: Record<string, any>) {
    if (DEFAULT_CONFIG.level <= LogLevel.ERROR) {
      console.error(...this.format('ERROR', message), error || '');
      captureError(error instanceof Error ? error : new Error(message), {
        module: this.moduleName,
        message,
        ...context,
      });
    }
  }
}

/**
 * 创建特定模块的 logger 实例
 * @example const logger = createLogger('PatientService');
 */
export const createLogger = (moduleName: string) => new Logger(moduleName);
